/**
 * PricingPage — Commercialization-ready plan comparison
 *
 * Gated behind ENABLE_PRICING_PAGE feature flag.
 * When flag is OFF, redirects to /membership.
 *
 * This page is PRESENTATION ONLY — it does not enforce entitlements,
 * call billing endpoints, or modify user state.
 */
import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { PLAN_CARDS, PLAN_FEATURES, PlanKey } from '../config/planPresentation';
import { CheckCircle2, X, ArrowLeft, Shield, Sparkles } from 'lucide-react';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const enabled = useFeatureFlag('enable_pricing_page');

  // Flag OFF → safe redirect to existing /membership page
  if (!enabled) {
    return <Navigate to="/membership" replace />;
  }

  // Derive user's current tier for highlighting
  const currentTier: PlanKey = (user as any)?.trial_active
    ? 'pro'
    : user?.plan_tier === 'premium'
      ? 'pro'
      : (user as any)?.peer_verification_status === 'verified' ||
        (user as any)?.peer_verification_status === 'verified_provisional'
        ? 'verified'
        : 'free';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="pricing-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Choose Your Plan
          </h1>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Stay ahead in medical literature. Pick the plan that fits your practice.
          </p>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLAN_CARDS.map((plan) => {
            const isCurrent = plan.key === currentTier;
            return (
              <div
                key={plan.key}
                className={`relative rounded-2xl border-2 p-6 flex flex-col transition-shadow ${
                  plan.highlight
                    ? `${plan.borderColor} shadow-lg ring-1 ring-sky-100`
                    : `${plan.borderColor} shadow-sm`
                } bg-gradient-to-b ${plan.gradient}`}
                data-testid={`plan-card-${plan.key}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      plan.highlight
                        ? 'bg-sky-600 text-white'
                        : 'bg-emerald-600 text-white'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan header */}
                <div className="mb-5 pt-2">
                  <h2 className={`text-xl font-bold ${plan.accentColor}`}>{plan.name}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">{plan.subtitle}</p>
                </div>

                {/* Price */}
                <div className="mb-5">
                  <div className={`text-3xl font-bold ${plan.accentColor}`}>{plan.price}</div>
                  <p className="text-xs text-slate-400 mt-1">{plan.priceNote}</p>
                </div>

                {/* Feature list */}
                <ul className="space-y-2.5 mb-6 flex-1">
                  {PLAN_FEATURES.map((feat) => {
                    const value = feat[plan.key];
                    const isIncluded = value === true || (typeof value === 'string' && value !== '');
                    const isExcluded = value === false;
                    return (
                      <li key={feat.label} className="flex items-start gap-2 text-sm">
                        {isExcluded ? (
                          <X className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                        ) : (
                          <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${
                            plan.highlight ? 'text-sky-500' : 'text-emerald-500'
                          }`} />
                        )}
                        <span className={isExcluded ? 'text-slate-400' : 'text-slate-700'}>
                          {feat.label}
                          {typeof value === 'string' && (
                            <span className="font-medium text-slate-900 ml-1">({value})</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <div className="py-2.5 px-4 rounded-lg bg-slate-100 text-center text-sm font-medium text-slate-500">
                    Current Plan
                  </div>
                ) : (
                  <button
                    onClick={() => navigate(plan.cta.href)}
                    className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
                      plan.highlight
                        ? 'bg-sky-600 hover:bg-sky-700 text-white shadow-sm'
                        : plan.key === 'verified'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-slate-800 hover:bg-slate-900 text-white'
                    }`}
                    data-testid={`plan-cta-${plan.key}`}
                  >
                    {plan.cta.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust / FAQ section */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-lg font-bold text-slate-800 mb-2">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-4">
          {[
            {
              q: 'What is the difference between Free and Verified?',
              a: 'Verified users have confirmed their clinical credentials, unlocking 3 literature queues and full community access. Verification is free.',
            },
            {
              q: 'What does the Pro trial include?',
              a: 'The 30-day Pro trial gives you full access to all features including 5 queues, LitStream audio, LitScholar AI, and library export.',
            },
            {
              q: 'Can I change my plan later?',
              a: 'Yes. You can upgrade or verify at any time from the Preferences or Pricing page.',
            },
            {
              q: 'Is my data safe?',
              a: 'LitCurrent follows PHI-Zero principles. We never process patient data. Your library and preferences are private to your account.',
            },
          ].map((item, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white"
              data-testid={`faq-${i}`}
            >
              <summary className="flex items-center justify-between px-5 py-3.5 cursor-pointer text-sm font-medium text-slate-800 select-none">
                {item.q}
                <Sparkles className="w-4 h-4 text-slate-300 group-open:text-sky-500 transition-colors shrink-0" />
              </summary>
              <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div className="text-center pb-12">
        <p className="text-xs text-slate-400">
          <Shield className="w-3 h-3 inline mr-1" />
          LitCurrent is for literature discussion only. Not clinical advice.
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
